import clsx from 'clsx';
import { ArrowRight, Mail, Lock } from 'lucide-react';

import HeroDemo from '../HeroDemo';
import Reveal, { useInView } from './Reveal';
import { useCountUp } from './useCountUp';

const BASE = import.meta.env.BASE_URL;
export const CONTACT = 'tonywilsonjesuraj@gmail.com';

/**
 * The landing page, told as the photographer's own evening.
 *
 * The order is the argument: the moment, the pile it leaves behind, the thing
 * that gets you through the pile, and only then what that thing costs. Every
 * section is either a photograph or a sentence, never a grid of boxed features,
 * because a page selling a photography tool that looks like a SaaS pricing deck
 * is telling the reader it was not made by a photographer.
 */

/** Eyebrow, headline, and the one sentence underneath. Same rhythm everywhere. */
function Heading({
  eyebrow,
  title,
  lead,
  size = 'section',
}: {
  eyebrow: string;
  title: React.ReactNode;
  lead?: React.ReactNode;
  size?: 'section' | 'mega';
}) {
  return (
    <div className="flex flex-col">
      <p className="label-mono">{eyebrow}</p>
      <h2
        className={clsx(
          'mt-6 max-w-4xl font-semibold text-balance text-ink-50',
          size === 'mega' ? 'text-mega' : 'text-section',
        )}
      >
        {title}
      </h2>
      {lead && <p className="mt-6 max-w-xl text-lead text-ink-400">{lead}</p>}
    </div>
  );
}

/** A hairline row: a mono label on the left, the sentence on the right. */
function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <li className="grid gap-2 border-t border-ink-850 py-6 sm:grid-cols-[minmax(0,10rem)_minmax(0,1fr)] sm:gap-8">
      <p className="label-mono pt-1">{label}</p>
      <p className="max-w-xl text-body leading-relaxed text-ink-300">{children}</p>
    </li>
  );
}

/* ================================================================== *
 * Hero: the keeper, and the card it came off
 * ================================================================== */

/**
 * One photograph, and underneath it the shoot it was pulled out of.
 *
 * A single beautiful frame says "photography" and nothing else. The strip is
 * what makes it the product: the argument of the whole page is that the frame
 * at the top was buried in the row at the bottom.
 */
export function HeroFrame({ photos }: { photos: string[] }) {
  const strip = Array.from({ length: 12 }, (_, i) => photos[i % Math.max(1, photos.length)]);

  return (
    /*
      Capped when the hero is stacked. Left to fill the column, one photograph
      becomes a full-screen plate on a tablet and pushes the whole argument
      below the fold; at plate size it reads as a print on a page.
    */
    <figure className="mx-auto flex w-full max-w-md flex-col gap-3 lg:max-w-none">
      <div className="relative overflow-hidden rounded-xl border border-ink-800 bg-ink-950">
        <img
          src={`${BASE}photos/kingfisher.jpg`}
          width={712}
          height={949}
          decoding="async"
          alt="A white-throated kingfisher perched on a bare branch against green foliage."
          className="aspect-[4/5] w-full animate-drift object-cover object-[center_58%]"
        />
        <span className="absolute left-4 top-4 rounded border border-keep-line bg-keep-soft/90 px-2.5 py-1 text-tiny font-medium text-keep backdrop-blur-sm">
          Kept
        </span>
      </div>

      <div className="flex gap-1" aria-hidden="true">
        {strip.map((url, i) => (
          <div
            key={i}
            className={clsx(
              'h-9 flex-1 overflow-hidden rounded-[3px] bg-ink-850 sm:h-12',
              i !== 4 && 'opacity-30',
              // Twelve thumbnails across a phone is a texture, not a filmstrip.
              i >= 8 && 'hidden sm:block',
            )}
          >
            {url && <img src={url} alt="" loading="lazy" className="h-full w-full object-cover" />}
          </div>
        ))}
      </div>

      <figcaption className="label-mono">One keeper. The rest of the shoot underneath.</figcaption>
    </figure>
  );
}

/* ================================================================== *
 * The aftermath
 * ================================================================== */

/** How many tiles the wall is built from. A card, not a sample. */
const WALL = 96;
/** The frame that survives, roughly two thirds of the way in. */
const SURVIVOR = 62;

export function Aftermath({ photos }: { photos: string[] }) {
  const frames = useCountUp(3000);
  const { ref, inView } = useInView<HTMLDivElement>(0.08);
  const wall = Array.from({ length: WALL }, (_, i) => photos[i % Math.max(1, photos.length)]);

  return (
    <section id="aftermath" className="border-t border-ink-850">
      <div className="mx-auto w-full max-w-content px-5 py-20 sm:px-8 sm:py-28">
        <Reveal>
          <Heading
            eyebrow="After the shoot"
            title="The moment is over. Now find the frame."
            lead="It is in there. So are four hundred that are very nearly it, shot half a second either side, and you have one evening."
          />
        </Reveal>

        {/*
          Ninety six frames, arriving one after another rather than all at once.
          A wall that simply appears is a texture; a wall that fills up is the
          card emptying onto your desk, which is the feeling the section is for.
        */}
        <div
          ref={ref}
          className="group mt-16 grid grid-cols-8 gap-[3px] sm:grid-cols-12 lg:grid-cols-[repeat(16,minmax(0,1fr))]"
        >
          {wall.map((url, i) => (
            <div
              key={i}
              style={{ transitionDelay: `${Math.min(i * 9, 900)}ms` }}
              className={clsx(
                'aspect-square overflow-hidden rounded-[2px] bg-ink-850 transition-opacity duration-500 ease-out motion-reduce:transition-none',
                !inView && 'opacity-0',
                inView && i === SURVIVOR && 'opacity-100 ring-1 ring-keep',
                inView &&
                  i !== SURVIVOR &&
                  'opacity-25 group-hover:opacity-45 motion-reduce:group-hover:opacity-25',
              )}
            >
              {url && <img src={url} alt="" loading="lazy" className="h-full w-full object-cover" />}
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-col gap-6 border-t border-ink-850 pt-8 sm:flex-row sm:items-baseline sm:justify-between">
          <p className="flex items-baseline gap-3">
            <span ref={frames.ref} className="tnum text-hero font-semibold text-ink-100">
              {frames.value.toLocaleString()}
            </span>
            <span className="label-mono">frames off one card</span>
          </p>
          <p className="max-w-md text-body leading-relaxed text-ink-400">
            One of them is the picture. Finding it is the part nobody photographs for.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ================================================================== *
 * AI suggests. You decide.
 * ================================================================== */

const ASSISTANT = [
  'Groups the frames you shot in one burst, so forty attempts at one moment arrive as one stack.',
  'Marks the sharpest frame in each stack with a badge.',
  'Flags the frames that came out soft.',
];

const PHOTOGRAPHER = [
  'Keep or reject. Every frame, every time, by your hand.',
  'Overrule the badge. It is a suggestion, and it does not get a vote.',
  'Switch the whole thing off. m3cull works exactly the same without it.',
];

export function Suggests({ photos }: { photos: string[] }) {
  const stack = photos.slice(4, 7);

  return (
    <section className="border-t border-ink-850 bg-ink-900/40">
      <div className="mx-auto w-full max-w-content px-5 py-20 sm:px-8 sm:py-28">
        <Reveal>
          <Heading
            eyebrow="What m3cull is"
            title={
              <>
                AI suggests. <span className="text-keep">You decide.</span>
              </>
            }
            lead="m3cull reads the shoot before you do and hands you what it noticed. It has never kept, rejected or moved a photograph on its own, and it is not going to start."
          />
        </Reveal>

        <div className="mt-16 grid gap-12 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] lg:gap-20">
          {/* ---- the two halves, side by side, so the split is the point ---- */}
          <Reveal>
            <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-1 lg:gap-12">
              <div>
                <p className="label-mono text-pick">The assistant</p>
                <ul className="mt-4 flex flex-col">
                  {ASSISTANT.map((line) => (
                    <li
                      key={line}
                      className="border-t border-ink-850 py-3.5 text-body leading-relaxed text-ink-300"
                    >
                      {line}
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <p className="label-mono text-keep">The photographer</p>
                <ul className="mt-4 flex flex-col">
                  {PHOTOGRAPHER.map((line) => (
                    <li
                      key={line}
                      className="border-t border-ink-850 py-3.5 text-body leading-relaxed text-ink-100"
                    >
                      {line}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Reveal>

          {/* ---- what a suggestion actually looks like ---- */}
          <Reveal delay={120}>
            <figure className="flex flex-col gap-4">
              <div className="overflow-hidden rounded-xl border border-ink-800 bg-ink-950">
                <p className="label-mono border-b border-ink-850 px-4 py-3">
                  One burst · 3 frames · stacked
                </p>

                <div className="grid grid-cols-3 gap-px bg-ink-850">
                  {stack.map((url, i) => (
                    <div key={i} className="relative aspect-[4/5] bg-ink-950">
                      {url ? (
                        <img
                          src={url}
                          alt=""
                          loading="lazy"
                          className={clsx(
                            'h-full w-full object-cover',
                            i !== 1 && 'opacity-60 saturate-[.65]',
                          )}
                        />
                      ) : (
                        <div className="checker h-full w-full" />
                      )}
                      {i === 1 && (
                        <>
                          <span className="pointer-events-none absolute inset-0 ring-2 ring-inset ring-pick" />
                          <span className="absolute bottom-2 left-2 rounded border border-pick-line bg-pick-soft/90 px-2 py-0.5 text-tiny font-medium text-pick backdrop-blur-sm">
                            Sharpest
                          </span>
                        </>
                      )}
                    </div>
                  ))}
                </div>

                <div className="flex items-center gap-2 border-t border-ink-850 px-4 py-3">
                  <span className="kbd">1</span>
                  <span className="text-caption text-ink-400">Keep</span>
                  <span className="kbd ml-3">2</span>
                  <span className="text-caption text-ink-400">Reject</span>
                  <span className="ml-auto hidden text-tiny text-ink-600 sm:inline">
                    Still yours to press.
                  </span>
                </div>
              </div>

              <figcaption className="max-w-lg text-caption leading-relaxed text-ink-500">
                The badge is the whole of it. m3cull points at one frame in the stack and then gets
                out of the way. Nothing is kept, rejected or moved until you press a key.
              </figcaption>
            </figure>
          </Reveal>
        </div>

        <Reveal delay={180}>
          <p className="mt-14 max-w-2xl border-t border-ink-850 pt-8 text-caption leading-relaxed text-ink-500">
            The model is small, open source and it runs in your browser on your own hardware. It
            comes down once, around 15 MB, and works offline after that. Your photographs are not
            uploaded before it runs, while it runs, or after. m3cull asks before starting it, and it
            can be declined or turned off in settings.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

/* ================================================================== *
 * The experience
 * ================================================================== */

const STEPS: [string, string][] = [
  ['Open a folder', 'Point m3cull at the card or the drive. Subfolders come with it.'],
  ['Cull', 'One key keeps, one key rejects. The shoot goes past at the speed you decide.'],
  ['Export', 'Keepers are copied into a folder you pick. The originals never move.'],
];

export function Experience() {
  return (
    <section id="work" className="border-t border-ink-850">
      <div className="mx-auto w-full max-w-content px-5 py-20 sm:px-8 sm:py-28">
        <Reveal>
          <Heading
            eyebrow="The culling itself"
            title="Keep. Reject. Next."
            lead="One key each. The frames after this one are already decoded, so there is nothing between you and the next decision."
          />
        </Reveal>

        {/*
          The real culling screen, running on real photographs, before anybody
          has clicked anything. A claim about speed is a claim; watching it is
          not.
        */}
        <Reveal delay={100}>
          <div className="mx-auto mt-14 max-w-4xl">
            <HeroDemo />
          </div>
        </Reveal>

        <Reveal delay={160}>
          <ol className="mt-16 grid gap-px sm:grid-cols-3">
            {STEPS.map(([title, body], i) => (
              <li key={title} className="border-t border-ink-850 pr-6 pt-6">
                <p className="label-mono">Step {i + 1}</p>
                <h3 className="mt-3 text-head font-semibold text-ink-50">{title}</h3>
                <p className="mt-2 max-w-xs text-body leading-relaxed text-ink-400">{body}</p>
              </li>
            ))}
          </ol>
        </Reveal>
      </div>
    </section>
  );
}

/* ================================================================== *
 * Privacy
 * ================================================================== */

export function Privacy() {
  return (
    <section id="privacy" className="border-t border-ink-850 bg-ink-900/40">
      <div className="mx-auto w-full max-w-content px-5 py-20 sm:px-8 sm:py-28">
        <Reveal>
          <Heading
            eyebrow="Privacy"
            title="Your photographs stay yours."
            lead="m3cull has no server. There is nowhere to upload a photograph to, which is a sturdier promise than a policy."
          />
        </Reveal>

        <Reveal delay={100}>
          <p className="mt-14 flex flex-wrap items-center gap-x-4 gap-y-2 border-y border-ink-850 py-6 font-mono text-caption text-ink-300">
            <span>your card</span>
            <ArrowRight size={14} className="text-ink-600" aria-hidden="true" />
            <span className="text-keep">your browser</span>
            <ArrowRight size={14} className="text-ink-600" aria-hidden="true" />
            <span>a folder you choose</span>
            <span className="text-ink-600" aria-hidden="true">
              ·
            </span>
            <span className="text-ink-600">nothing else, at any point</span>
          </p>
        </Reveal>

        <Reveal delay={160}>
          <ul className="mt-12 flex flex-col border-b border-ink-850">
            <Row label="On disk">
              Photographs are read straight off your card or drive by the browser. m3cull never
              copies them anywhere you did not name.
            </Row>
            <Row label="On your machine">
              The AI runs locally, in your own browser. The model comes down once. Your photographs
              never go up.
            </Row>
            <Row label="In this browser">
              Culling decisions are saved here and nowhere else, which is why a reload picks up where
              you left off. No account. No cookies. No tracking.
            </Row>
          </ul>
        </Reveal>

        <Reveal delay={220}>
          <p className="label-mono mt-8">
            No account · No uploads · No cloud processing · No server holding your work
          </p>
        </Reveal>
      </div>
    </section>
  );
}

/* ================================================================== *
 * Free
 * ================================================================== */

const COMPARISON: { label: string; mine: string; pm: string; as: string }[] = [
  { label: 'Price', mine: 'Free', pm: '$149 a year, or $299 once', as: 'Subscription' },
  { label: 'Install', mine: 'A browser tab', pm: 'Desktop app', as: 'Desktop app' },
  { label: 'Bursts', mine: 'Grouped, sharpest suggested', pm: 'Not offered', as: 'Grouped for you' },
  { label: 'Who picks the keepers', mine: 'You do', pm: 'You do', as: 'The AI does' },
  { label: 'Photos leave your machine', mine: 'No', pm: 'No', as: 'No' },
];

export function Price() {
  return (
    <section id="price" className="border-t border-ink-850">
      <div className="mx-auto w-full max-w-content px-5 py-20 sm:px-8 sm:py-28">
        <Reveal>
          <Heading
            eyebrow="Price"
            size="mega"
            title={
              <>
                Free. <span className="text-ink-500">Actually free.</span>
              </>
            }
            lead="No subscription, no credits, no trial running out, no watermark, no card. There is no paid tier to be nudged towards, because there is no paid tier."
          />
        </Reveal>

        <Reveal delay={100}>
          <p className="mt-8 max-w-2xl text-body leading-relaxed text-ink-400">
            Forever? I hope so, but I will not promise what I cannot control. Here is what I will
            promise: everything m3cull does today stays free. If bigger things get built later, those
            might cost money. This part will not.
          </p>
        </Reveal>

        {/* ---- the honest comparison, as an editorial table rather than a grid of cards ---- */}
        <Reveal delay={160}>
          <div className="mt-16 overflow-x-auto">
            <table className="w-full min-w-[600px] border-collapse text-left">
              <caption className="sr-only">
                m3cull compared with Photo Mechanic and Aftershoot
              </caption>
              <thead>
                <tr className="border-b border-ink-700">
                  <th scope="col" className="label-mono py-4 pr-6 font-normal">
                    <span className="sr-only">What is being compared</span>
                  </th>
                  <th scope="col" className="py-4 pr-6 text-head font-semibold text-ink-50">
                    m3cull
                  </th>
                  <th scope="col" className="py-4 pr-6 text-head font-medium text-ink-400">
                    Photo Mechanic
                  </th>
                  <th scope="col" className="py-4 text-head font-medium text-ink-400">
                    Aftershoot
                  </th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON.map((row) => (
                  <tr key={row.label} className="border-b border-ink-850">
                    <th scope="row" className="label-mono py-4 pr-6 align-top font-normal">
                      {row.label}
                    </th>
                    <td className="py-4 pr-6 align-top text-caption text-keep">{row.mine}</td>
                    <td className="py-4 pr-6 align-top text-caption text-ink-500">{row.pm}</td>
                    <td className="py-4 align-top text-caption text-ink-500">{row.as}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Reveal>

        <Reveal delay={200}>
          <p className="mt-6 max-w-2xl text-caption leading-relaxed text-ink-600">
            Their prices were checked in August 2026 and are theirs to change, so check before you
            decide. All three keep your photographs local. What differs is the bill, the install, and
            who does the choosing.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

/* ================================================================== *
 * Who made this
 * ================================================================== */

export function Maker({ photo }: { photo?: string }) {
  return (
    <section className="border-t border-ink-850 bg-ink-900/40">
      <div className="mx-auto grid w-full max-w-content items-center gap-12 px-5 py-20 sm:px-8 sm:py-28 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] lg:gap-20">
        <Reveal>
          <div className="flex flex-col gap-6">
            <p className="label-mono">Who made this, and why it is free</p>

            <blockquote className="border-l-2 border-ink-700 pl-5">
              <p className="text-section font-semibold text-balance text-ink-50">
                Taking photos is the fun part. Sorting four hundred of them afterwards is paperwork
                with a shutter count.
              </p>
            </blockquote>

            <p className="max-w-xl text-lead leading-relaxed text-ink-300">
              Hi. I am <span className="text-ink-50">Tony Wilson Jesuraj</span>. I take photographs
              to satisfy my soul, and I write software to satisfy my soul and my pocket. I built
              m3cull because I got tired of the second half of my own hobby.
            </p>

            <p className="max-w-xl text-body leading-relaxed text-ink-400">
              It collects nothing for two honest reasons: storing your data would cost me money I do
              not have, and I do not want your data. Right now I would rather a hundred photographers
              used this and told me what broke than one paid for it and quietly gave up.
            </p>

            <div className="flex flex-wrap items-center gap-2.5 pt-1">
              {/* A real <a>: /about is a static page outside the app. */}
              <a className="btn-outline px-5 py-2.5 text-body" href="/about">
                Read the rest
                <ArrowRight size={15} />
              </a>
              <a
                className="btn-ghost px-4 py-2.5 text-body"
                href={`mailto:${CONTACT}?subject=${encodeURIComponent('m3cull feedback')}`}
              >
                <Mail size={15} />
                {CONTACT}
              </a>
            </div>
          </div>
        </Reveal>

        <Reveal delay={120}>
          <figure className="flex flex-col gap-3">
            <div className="aspect-[4/3] w-full overflow-hidden rounded-xl border border-ink-800 bg-ink-950">
              {photo ? (
                <img src={photo} alt="" loading="lazy" className="h-full w-full object-cover" />
              ) : (
                <div className="checker h-full w-full" />
              )}
            </div>
            <figcaption className="text-tiny leading-relaxed text-ink-500">
              The kingfisher at the top of this page is mine. The rest of the photographs here are
              from the sample shoot: U.S. Fish and Wildlife Service, public domain.
            </figcaption>
          </figure>
        </Reveal>
      </div>
    </section>
  );
}

/* ================================================================== *
 * Who it is for
 * ================================================================== */

export function WhoFor({ photos }: { photos: string[] }) {
  const audiences = [
    {
      label: 'Wildlife',
      line: 'You shoot through the moment, because there is no way to know from behind the camera which frame will be the one.',
      image: photos[0],
    },
    {
      label: 'Sports',
      line: 'The play lasts two seconds. You hold the shutter down and sort it out at home.',
      image: `${BASE}site/sports.jpg`,
    },
    {
      label: 'Weddings & events',
      line: 'Nobody is doing that again for the camera, so you take all of it and choose later.',
      image: `${BASE}site/events.jpg`,
    },
    {
      label: 'Travel & everyday',
      line: 'You came home with the memories. Now you have to find them.',
      image: `${BASE}site/travel.jpg`,
    },
  ];

  return (
    <section className="border-t border-ink-850">
      <div className="mx-auto w-full max-w-content px-5 py-20 sm:px-8 sm:py-28">
        <Reveal>
          <Heading
            eyebrow="Who it is for"
            title="Anywhere the moment only happens once."
            lead="Different subjects, same evening. If one outing fills a card, the slow part was never the shooting."
          />
        </Reveal>

        <ul className="mt-14 flex flex-col border-b border-ink-850">
          {audiences.map((audience, i) => (
            <Reveal key={audience.label} delay={i * 70} as="li">
              <div className="group grid items-center gap-5 border-t border-ink-850 py-6 sm:grid-cols-[5rem_minmax(0,10rem)_minmax(0,1fr)] sm:gap-8">
                <div className="h-20 w-20 shrink-0 overflow-hidden rounded bg-ink-900">
                  {audience.image ? (
                    <img
                      src={audience.image}
                      alt=""
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.08] motion-reduce:transition-none"
                    />
                  ) : (
                    <div className="checker h-full w-full" />
                  )}
                </div>
                <p className="label-mono">{audience.label}</p>
                <p className="max-w-xl text-body leading-relaxed text-ink-300">{audience.line}</p>
              </div>
            </Reveal>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* ================================================================== *
 * The last word
 * ================================================================== */

export function FinalCta({
  onStart,
  onDemo,
  busy,
}: {
  onStart: () => void;
  onDemo: () => void;
  busy?: boolean;
}) {
  return (
    <section className="border-t border-ink-850">
      <div className="mx-auto w-full max-w-content px-5 py-28 sm:px-8 sm:py-40">
        <Reveal>
          <h2 className="max-w-4xl text-mega font-semibold text-balance text-ink-50">
            You captured the moment.{' '}
            <span className="text-ink-500">Now find the frame.</span>
          </h2>

          <div className="mt-12 flex flex-wrap items-center gap-3">
            <button className="btn-primary px-6 py-3 text-body" onClick={onStart} disabled={busy}>
              Start Culling
            </button>
            <button className="btn-outline px-6 py-3 text-body" onClick={onDemo} disabled={busy}>
              Try a sample shoot
            </button>
          </div>

          <p className="label-mono mt-8">
            Free · No account · Nothing uploaded · Chrome, Edge, Brave or Arc
          </p>
        </Reveal>
      </div>
    </section>
  );
}

/**
 * The line that closes the about page, repeated here.
 *
 * The footer below states the same facts as three labelled columns, which is
 * the right shape for a footer and the wrong one for a last word. This is the
 * only place on the landing page where the whole promise is one sentence, and
 * it hands the reader to the two static pages instead of leaving them at the
 * bottom of a marketing page.
 */
export function SiteOutro() {
  return (
    <div className="mx-auto w-full max-w-content px-5 sm:px-8">
      <hr className="border-0 border-t border-ink-850" />
      <p className="max-w-3xl py-7 text-caption leading-relaxed text-ink-500">
        m3cull is free, it has no account, and it never uploads a photograph. If you want the long
        version, the{' '}
        <a className="text-keep transition hover:underline" href="/faq">
          FAQ
        </a>{' '}
        is more thorough, and{' '}
        <a className="text-keep transition hover:underline" href="/about">
          the story behind it
        </a>{' '}
        is considerably less serious.
      </p>
    </div>
  );
}

export function SiteFooter() {
  return (
    <footer className="border-t border-ink-850">
      <div className="mx-auto flex w-full max-w-content flex-col gap-8 px-5 py-12 sm:flex-row sm:items-start sm:justify-between sm:px-8">
        <div className="max-w-sm">
          <p className="text-head font-semibold text-ink-50">m3cull</p>
          <p className="mt-2 text-caption leading-relaxed text-ink-500">
            Fast photo culling that runs entirely in your browser. Free, local, and with nothing to
            install.
          </p>
        </div>

        <div className="flex flex-col gap-2 text-caption">
          <p className="label-mono mb-1">Contact</p>
          <a className="text-ink-300 transition hover:text-ink-50" href={`mailto:${CONTACT}`}>
            {CONTACT}
          </a>
          {/* Real <a> tags, not router links: both are static pages outside the app. */}
          <a className="text-ink-300 transition hover:text-ink-50" href="/faq">
            Questions and answers
          </a>
          <a className="text-ink-300 transition hover:text-ink-50" href="/about">
            About, and who made this
          </a>
        </div>

        <div className="flex max-w-xs flex-col gap-2 text-caption">
          <p className="label-mono mb-1">Privacy</p>
          <p className="flex items-start gap-2 leading-relaxed text-ink-500">
            <Lock size={13} className="mt-1 shrink-0 text-ink-600" />
            No account, no tracking, no uploads. Culling decisions are saved in this browser and
            nowhere else.
          </p>
        </div>
      </div>
    </footer>
  );
}
